import { type Order } from '@/Components/EntityTable/orderParam'
import { DataClassName } from '@/Data/const'

/**
 * Marker the provider replaces with the logged-in user's contactId at runtime.
 * Using a branded string constant avoids `as` casts at lookup sites.
 */
export const CURRENT_USER = '$currentUserId' as const
export type CurrentUserMarker = typeof CURRENT_USER

export interface ListConfig {
  defaultOrder?: Order
  searchKey?: string
  /**
   * Programmatic PISA scope applied before user filters. Values may be the
   * `CURRENT_USER` marker, which the provider replaces with the real contactId.
   */
  scope?: Record<string, string | boolean | CurrentUserMarker>
}

/**
 * Per-entity config registry.
 *
 * Keyed as `Partial<Record<string, ListConfig>>` (not `DataClassName`) so the
 * provider can look up by the plain string from `dataScope.dataClassName()`
 * without an `as` cast (N4). Entries are written with `DataClassName.X` keys
 * for readability — the enum values are strings.
 *
 * ⚠️ Verify keys match the actual `dataScope.dataClassName()` at runtime (see
 * Step 8 provider notes). `activity_type` value copied verbatim from current
 * code — correctness is pre-existing.
 */
export const LIST_CONFIG: Partial<Record<string, ListConfig>> = {
  [DataClassName.ACTIVITY]: {
    defaultOrder: ['beginAt', 'desc'],
  },
  [DataClassName.ACTIVITY_CONTACT_RELATION]: {
    defaultOrder: ['activity_beginAt', 'asc'],
    searchKey: 'activity_search',
    scope: {
      agent: true,
      contact: CURRENT_USER,
      activity_released: true,
      activity_open: true,
      activity_type: 'TASK APPOINTMENT CALL',
    },
  },
  [DataClassName.MY_WORK_CONTACT_RELATION]: {
    scope: { father: CURRENT_USER },
  },
}

/**
 * The deliberate default for any entity overview without a specific entry: sort
 * by most-recently-updated. `DXPOverview` is generic (it renders whichever
 * dataClass the page binds), so a shared default — rather than ~10 duplicate
 * entries — keeps every overview, including future ones, sorted sensibly.
 * Override per-entity in `LIST_CONFIG` only when the default differs.
 */
export const DEFAULT_LIST_CONFIG: ListConfig = {
  defaultOrder: ['updatedAt', 'desc'],
}

export const getListConfig = (dataClassName: string): ListConfig =>
  LIST_CONFIG[dataClassName] ?? DEFAULT_LIST_CONFIG
